import { ColumnDef } from '@tanstack/react-table';
import { OutreachProgram } from '../types/outreach';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { MoreHorizontal, Users, FileText, Edit2, BarChart, Play, CheckCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export const columns = (
  onAction: (action: string, program: OutreachProgram) => void
): ColumnDef<OutreachProgram>[] => [
  {
    accessorKey: 'name',
    header: 'Program Name',
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => {
      const type = row.getValue('type') as string;
      return (
        <Badge variant="secondary" className="capitalize">
          {type.replace('-', ' ')}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as 'planned' | 'active' | 'completed' | 'cancelled';
      const variants: Record<typeof status, 'secondary' | 'default' | 'destructive' | 'outline'> = {
        planned: 'secondary',
        active: 'default',
        completed: 'outline',
        cancelled: 'destructive',
      };
      return (
        <Badge variant={variants[status]} className="capitalize">
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'startDate',
    header: 'Start Date',
    cell: ({ row }) => format(new Date(row.getValue('startDate')), 'MMM d, yyyy'),
  },
  {
    accessorKey: 'location',
    header: 'Location',
    cell: ({ row }) => row.original.location.name,
  },
  {
    accessorKey: 'participants',
    header: 'Participants',
    cell: ({ row }) => {
      const actual = row.original.actualParticipants;
      const target = row.original.targetParticipants;
      return (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span>{actual || 0}</span>
          <span className="text-muted-foreground">/ {target}</span>
        </div>
      );
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const program = row.original;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction('view', program)}>
              <FileText className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction('edit', program)}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Program
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction('report', program)}>
              <BarChart className="h-4 w-4 mr-2" />
              View Reports
            </DropdownMenuItem>
            {program.status === 'planned' && (
              <DropdownMenuItem onClick={() => onAction('start', program)}>
                <Play className="h-4 w-4 mr-2" />
                Start Program
              </DropdownMenuItem>
            )}
            {program.status === 'active' && (
              <DropdownMenuItem onClick={() => onAction('complete', program)}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Program
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
